<template>
  <div class="statistics-page">
    <div class="time-filter">
      <button 
        v-for="period in periods" 
        :key="period.value"
        :class="['period-btn', { active: selectedPeriod === period.value }]"
        @click="selectedPeriod = period.value"
      >
        {{ period.label }}
      </button>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-header">订单统计</div>
        <div class="stat-value">{{ stats.orderCount }}</div>
        <div class="stat-change">较上期 {{ stats.orderChange }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-header">营收总额</div>
        <div class="stat-value">{{ stats.revenue }}</div>
        <div class="stat-change">较上期 {{ stats.revenueChange }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-header">用户增长</div>
        <div class="stat-value">{{ stats.userGrowth }}</div>
        <div class="stat-change">较上期 {{ stats.userChange }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-header">跑腿员活跃</div>
        <div class="stat-value">{{ stats.activeRunners }}</div>
        <div class="stat-change">较上期 {{ stats.runnerChange }}</div>
      </div>
    </div>

    <div class="charts-row">
      <div class="chart-card">
        <h3>订单趋势</h3>
        <div class="chart-container">
          <canvas id="orderTrendChart"></canvas>
        </div>
      </div>
      <div class="chart-card">
        <h3>服务类型占比</h3>
        <div class="chart-container">
          <canvas id="servicePieChart"></canvas>
        </div>
      </div>
    </div>

    <div class="charts-row">
      <div class="chart-card full-width">
        <h3>营收趋势</h3>
        <div class="chart-container">
          <canvas id="revenueChart"></canvas>
        </div>
      </div>
    </div>

    <div class="bottom-row">
      <div class="ranking-card">
        <h3>小区订单排行</h3>
        <table>
          <thead>
            <tr>
              <th>排名</th>
              <th>小区名称</th>
              <th>订单数</th>
              <th>占比</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in communityRanking" :key="index">
              <td :class="'rank ' + (index < 3 ? 'top' : '')">{{ index + 1 }}</td>
              <td>{{ item.name }}</td>
              <td>{{ item.count }}</td>
              <td>{{ item.percentage }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="ranking-card">
        <h3>跑腿员接单排行</h3>
        <table>
          <thead>
            <tr>
              <th>排名</th>
              <th>跑腿员</th>
              <th>接单数</th>
              <th>评分</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in runnerRanking" :key="index">
              <td :class="'rank ' + (index < 3 ? 'top' : '')">{{ index + 1 }}</td>
              <td>{{ item.name }}</td>
              <td>{{ item.count }}</td>
              <td>⭐{{ item.rating }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Statistics',
  data() {
    return {
      selectedPeriod: 'week',
      periods: [
        { label: '今日', value: 'today' },
        { label: '本周', value: 'week' },
        { label: '本月', value: 'month' },
        { label: '本季度', value: 'quarter' },
        { label: '本年', value: 'year' }
      ],
      stats: {
        orderCount: 1280,
        orderChange: '+12.5%',
        revenue: 28560.50,
        revenueChange: '+8.3%',
        userGrowth: 125,
        userChange: '+15.2%',
        activeRunners: 32,
        runnerChange: '-2.4%'
      },
      communityRanking: [
        { name: '幸福小区', count: 420, percentage: 32.8 },
        { name: '阳光花园', count: 356, percentage: 27.8 },
        { name: '蓝天小区', count: 285, percentage: 22.3 },
        { name: '星河湾', count: 145, percentage: 11.3 },
        { name: '绿城花园', count: 74, percentage: 5.8 }
      ],
      runnerRanking: [
        { name: '王师傅', count: 156, rating: 4.9 },
        { name: '张师傅', count: 132, rating: 4.8 },
        { name: '李师傅', count: 118, rating: 4.7 },
        { name: '赵师傅', count: 98, rating: 4.9 },
        { name: '刘师傅', count: 86, rating: 4.6 }
      ]
    }
  },
  mounted() {
    this.renderCharts()
  },
  renderCharts() {
    const orderData = [180, 220, 195, 280, 310, 255, 290]
    const days = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
    const revenueData = [4200, 5100, 4800, 6200, 7100, 5800, 6860.5]
    
    const serviceData = [45, 30, 20, 5]
    const services = ['代送物品', '代买物品', '代取快递', '其他']

    const renderBarChart = (id, data, labels, color = '#3498db') => {
      const canvas = document.createElement('canvas')
      canvas.id = id
      canvas.width = 600
      canvas.height = 250
      document.getElementById(id)?.replaceWith(canvas)
      
      const ctx = canvas.getContext('2d')
      const width = canvas.width
      const height = canvas.height
      const padding = { top: 30, right: 30, bottom: 40, left: 50 }
      const chartWidth = width - padding.left - padding.right
      const chartHeight = height - padding.top - padding.bottom
      
      ctx.clearRect(0, 0, width, height)
      
      const maxVal = Math.max(...data) * 1.2
      const barWidth = chartWidth / data.length * 0.6
      const gap = chartWidth / data.length * 0.4
      
      data.forEach((val, i) => {
        const barHeight = (val / maxVal) * chartHeight
        const x = padding.left + i * (barWidth + gap) + gap / 2
        const y = padding.top + chartHeight - barHeight
        
        const gradient = ctx.createLinearGradient(x, y, x, y + barHeight)
        gradient.addColorStop(0, color)
        gradient.addColorStop(1, this.adjustColor(color, -30))
        
        ctx.fillStyle = gradient
        ctx.fillRect(x, y, barWidth, barHeight)
        
        ctx.fillStyle = '#666'
        ctx.font = '12px Arial'
        ctx.textAlign = 'center'
        ctx.fillText(labels[i], x + barWidth / 2, height - 15)
        ctx.fillText(val.toString(), x + barWidth / 2, y - 8)
      })
      
      ctx.strokeStyle = '#eee'
      ctx.lineWidth = 1
      for (let i = 0; i <= 4; i++) {
        const y = padding.top + (chartHeight / 4) * i
        ctx.beginPath()
        ctx.moveTo(padding.left, y)
        ctx.lineTo(width - padding.right, y)
        ctx.stroke()
        
        ctx.fillStyle = '#999'
        ctx.font = '11px Arial'
        ctx.textAlign = 'right'
        ctx.fillText(Math.round(maxVal - (maxVal / 4) * i), padding.left - 10, y + 4)
      }
    }

    const renderPieChart = (id, data, labels) => {
      const canvas = document.createElement('canvas')
      canvas.id = id
      canvas.width = 350
      canvas.height = 250
      document.getElementById(id)?.replaceWith(canvas)
      
      const ctx = canvas.getContext('2d')
      const centerX = canvas.width / 2
      const centerY = canvas.height / 2
      const radius = 80
      
      const colors = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12']
      let startAngle = 0
      
      data.forEach((val, i) => {
        const sliceAngle = (val / data.reduce((a,b) => a+b, 0)) * Math.PI * 2
        ctx.beginPath()
        ctx.moveTo(centerX, centerY)
        ctx.arc(centerX, centerY, radius, startAngle, startAngle + sliceAngle)
        ctx.closePath()
        ctx.fillStyle = colors[i]
        ctx.fill()
        startAngle += sliceAngle
      })
      
      ctx.fillStyle = '#fff'
      ctx.beginPath()
      ctx.arc(centerX, centerY, radius / 2.5, 0, Math.PI * 2)
      ctx.fill()
      
      ctx.fillStyle = '#333'
      ctx.font = '14px Arial'
      ctx.textAlign = 'center'
      ctx.fillText('服务分布', centerX, centerY - 8)
      ctx.fillText(`${data.reduce((a,b) => a+b, 0)}%`, centerX, centerY + 12)
      
      let legendY = 40
      labels.forEach((label, i) => {
        ctx.fillStyle = colors[i]
        ctx.fillRect(centerX - 70, legendY, 12, 12)
        ctx.fillStyle = '#333'
        ctx.font = '12px Arial'
        ctx.textAlign = 'left'
        ctx.fillText(`${label} (${data[i]}%)`, centerX - 55, legendY + 10)
        legendY += 20
      })
    }

    renderBarChart('orderTrendChart', orderData, days)
    renderPieChart('servicePieChart', serviceData, services)
    renderBarChart('revenueChart', revenueData, days, '#e74c3c')
  },
  adjustColor(color, amount) {
    const hex = color.replace('#', '')
    const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount))
    const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount))
    const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount))
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
  }
}
</script>

<style scoped>
.statistics-page {
  padding: 20px;
}

.time-filter {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.period-btn {
  padding: 10px 20px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.period-btn.active {
  background: #3498db;
  color: #fff;
  border-color: #3498db;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

.stat-header {
  font-size: 14px;
  color: #999;
  margin-bottom: 10px;
}

.stat-value {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
}

.stat-change {
  font-size: 13px;
  color: #2ecc71;
}

.stat-change.negative {
  color: #e74c3c;
}

.charts-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.chart-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

.chart-card.full-width {
  grid-column: span 2;
}

.chart-card h3 {
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #333;
}

.chart-container {
  display: flex;
  justify-content: center;
  overflow-x: auto;
}

.bottom-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.ranking-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

.ranking-card h3 {
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #333;
}

.ranking-card table {
  width: 100%;
  border-collapse: collapse;
}

.ranking-card th, .ranking-card td {
  padding: 10px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.ranking-card th {
  color: #999;
  font-weight: 500;
}

.rank {
  width: 30px;
  text-align: center;
}

.rank.top {
  font-weight: 600;
}

.rank.top:nth-child(1) { color: #e74c3c; }
.rank.top:nth-child(2) { color: #f39c12; }
.rank.top:nth-child(3) { color: #2ecc71; }
</style>